import {
  type ApproverDecision,
  ApproverDecisionSchema,
  type AssessmentState,
  AssessmentStateSchema,
  type BatchDeclaration,
  type DecisionBatch,
  DecisionBatchSchema,
  type PersistedFinding,
  PersistedFindingSchema,
} from '@zevik/schemas';
import type { DecisionRequest } from '../review/decision';

/**
 * Everything this workspace reads and writes (docs/DECISIONS.md ZD-112).
 *
 * ## One data path with two sources
 *
 * The fixture is served from `public/fixtures/` and arrives over the **same
 * `fetch`** as the live routes, so there is no demo mode beside a real one — the
 * parsing, the error handling and the shapes are exercised identically whichever
 * source is in use. With no parameter the page's own origin is read (ZD-116
 * §6.2); `?fixture` asks for the fixture and `?api=<base>` for an API elsewhere.
 *
 * That also keeps the fixture out of the bundle. Imported as a module under
 * `src/` it would be inlined, and it is 516 KB.
 *
 * ## Both keys come from the route and are never derived here
 *
 * `finding_key` and `finding_structural_key` are read off the payload. **A
 * browser must not compute either**, and the reason is measured rather than
 * stylistic (ZD-111 §1): `finding_structural_key` needs `statement.subject_id`,
 * the subject is a member of `grounded_object_ids` on 102 of 102 corpus findings
 * so the set never looks unavailable, and it has more than one member on 17 of
 * 102 — so a client picking from it is right on 85 with every one of those 85
 * reading as confirmation, and a wrong structural key makes `classifyOrphans`
 * report a **moved** decision as **withdrawn**. `client.test.ts` asserts this
 * module names neither key-deriving function.
 *
 * ## What is parsed and what is not
 *
 * Findings and decisions go through the schemas the routes validate against, so a
 * route that returned the right shape with the wrong content fails here rather
 * than rendering. The clarification payload is **not** parsed: `AnswerLedger` is
 * an interface rather than a Zod schema in `@zevik/packs`, and this app may not
 * import that package — so it is read structurally and that is stated rather than
 * hidden behind a cast that would look like validation.
 */

export class ReadFailedError extends Error {
  constructor(
    readonly what: string,
    readonly status: number,
    /** The API's own sentence, kept apart so a page can show it without this class's framing. */
    readonly detail: string,
  ) {
    super(`could not read ${what}: the API answered ${String(status)}. ${detail}`);
    this.name = 'ReadFailedError';
  }
}

export class DecisionRefusedError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(`the decision was not recorded: the API answered ${String(status)}. ${detail}`);
    this.name = 'DecisionRefusedError';
  }
}

/** One issue the API raised against a batch, with the member it is about where it is about one. */
export interface BatchIssue {
  path: (string | number)[];
  message: string;
}

/**
 * A batch the API refused as a set (docs/DECISIONS.md ZD-127 §2): the
 * request schema's own issues, kept with their paths so the screen can say
 * which finding an issue is about by title rather than by position.
 */
export class BatchRefusedError extends DecisionRefusedError {
  constructor(
    status: number,
    detail: string,
    readonly issues: BatchIssue[],
  ) {
    super(status, detail);
    this.name = 'BatchRefusedError';
  }
}

/** One entry of `GET /v1/assessments/:id/findings`. */
export interface FindingEntry extends PersistedFinding {
  finding_key: string;
  finding_structural_key: string;
}

/**
 * One finding entry, parsed.
 *
 * **Per entry rather than through a wrapper schema, and no `z` import.** Building
 * `z.object({findings: z.array(...)})` here would put a second schema in front of
 * the route's own and would name no entry when it failed; this parses each with
 * `PersistedFindingSchema` — the schema the route validates against — and says
 * which index was wrong.
 *
 * The two keys are checked for presence and **never computed**. See this module's
 * header for the measurement behind that.
 */
function parseFindings(payload: unknown, what: string): FindingEntry[] {
  const findings = (payload as { findings?: unknown }).findings;
  if (!Array.isArray(findings)) {
    throw new ReadFailedError(what, 200, 'The response carried no findings array.');
  }
  return findings.map((entry, index) => {
    const row = entry as { finding_key?: unknown; finding_structural_key?: unknown };
    const persisted = PersistedFindingSchema.parse(entry);
    if (typeof row.finding_key !== 'string' || row.finding_key.length === 0) {
      throw new ReadFailedError(
        what,
        200,
        `Finding ${String(index)} carried no finding_key. A workspace may not compute one.`,
      );
    }
    if (typeof row.finding_structural_key !== 'string' || row.finding_structural_key.length === 0) {
      throw new ReadFailedError(
        what,
        200,
        `Finding ${String(index)} carried no finding_structural_key. A workspace may not ` +
          `compute one: the subject is not recoverable from the finding alone.`,
      );
    }
    return {
      ...persisted,
      finding_key: row.finding_key,
      finding_structural_key: row.finding_structural_key,
    };
  });
}

/** The header, of which read-and-decide needs the id and the state. */
function parseAssessment(payload: unknown): {
  assessment_id: string;
  state: AssessmentState;
} {
  const header = (payload as { header?: { assessment_id?: unknown; state?: unknown } }).header;
  if (typeof header?.assessment_id !== 'string') {
    throw new ReadFailedError('the assessment', 200, 'The response carried no header.');
  }
  return {
    assessment_id: header.assessment_id,
    state: AssessmentStateSchema.parse(header.state),
  };
}
/** `{decisions, standing}`, both parsed through the schema `/decisions` validates against. */
function parseReviewLog(payload: unknown): {
  decisions: ApproverDecision[];
  standing: ApproverDecision[];
  batches: DecisionBatch[];
} {
  const body = payload as { decisions?: unknown; standing?: unknown; batches?: unknown };
  const parseList = (value: unknown, field: string): ApproverDecision[] => {
    if (!Array.isArray(value)) {
      throw new ReadFailedError('the review log', 200, `The response carried no ${field} array.`);
    }
    return value.map((decision) => ApproverDecisionSchema.parse(decision));
  };
  return {
    decisions: parseList(body.decisions, 'decisions'),
    /*
     * **Read, never re-derived.** `standing` is the last decision per
     * `finding_key`, resolved by `standingDecisions` in the orchestrator. A
     * workspace computing it would be the third expression of that rule, and the
     * invisible kind: on any history where nobody changed their mind the log and
     * the standing set agree everywhere, so a wrong implementation would look
     * right (ZD-111 §3).
     */
    standing: parseList(body.standing, 'standing'),
    /*
     * The groups, beside the log (ZD-127 §1): a batched decision carries its
     * group's id and may carry no reason of its own, so the reason a reviewer
     * gave for the group is read from here. Required, not defaulted — a log
     * with no `batches` array is a route this workspace does not know.
     */
    batches: (() => {
      if (!Array.isArray(body.batches)) {
        throw new ReadFailedError('the review log', 200, 'The response carried no batches array.');
      }
      return body.batches.map((batch) => DecisionBatchSchema.parse(batch));
    })(),
  };
}

/** One ask a client is being put, and what has been said about it. */
export interface LedgerQuestion {
  key: string;
  object_id: string;
  attribute: string;
  /** The most recent recorded answer, or `null` where none has been given. */
  answer: unknown;
  bearings: { threat_pattern_id: string; subject_id: string }[];
}

export interface ClarificationsResponse {
  clarifications: {
    threat_pattern_id: string;
    subject_id: string;
    statement: unknown;
  }[];
  ledger: {
    questions: LedgerQuestion[];
    findings: {
      finding_id: string;
      finding_structural_key: string;
      outstanding: { object_id: string; attribute: string }[];
      discharged: boolean;
    }[];
  };
}

export interface Workspace {
  assessment: { assessment_id: string; state: AssessmentState };
  findings: FindingEntry[];
  clarifications: ClarificationsResponse;
  reviewLog: {
    decisions: ApproverDecision[];
    standing: ApproverDecision[];
    batches: DecisionBatch[];
  };
}

/** Where the data comes from, and what the page claims about who is reading. */
export interface Source {
  /**
   * A live API base, or `null` to read the served fixture. **The default is the
   * empty string — the page's own origin**, which is where a deployment serving
   * this workspace as static assets puts the API (ZD-116 §4), and locally
   * where Vite proxies `/v1` to `wrangler dev`. The first version made the
   * fixture the default and reached the live origin through `?api=` with an
   * empty value, and the first live run showed the fixture at exactly that
   * URL: an empty-valued query parameter is what a redirect rebuilder drops,
   * and on a deployment the URL goes through Access's (ZD-116 §6.2). So the
   * live origin needs no parameter, `?fixture` is how the fixture is asked
   * for, and `?api=<base>` still points at an API elsewhere.
   */
  base: string | null;
  /**
   * The assessment the page is about, or `null` where the URL names none —
   * which is the landing page (ZD-124 §2). This used to default to the
   * fixture's `assessment_vetchford`, and on a deployment that default was
   * the 404 a signed-in person met first (ZD-120 §2). The fixture ignores it:
   * `?fixture` is one assessment at the gate whichever id is beside it.
   */
  assessmentId: string | null;
  /**
   * Identity claims, each sent **only where the URL states one** (ZD-116 §3).
   * Under `dev-header` the tenant and user are what the API resolves from,
   * so a local URL carries them; under a real credential every one of them is
   * a claim the record checks, and a default here — the `tenant_demo` /
   * `user_reviewer` / `approver` this used to send unasked — would name a user
   * who is not the person signed in and be refused. `GET /v1/me` is where the
   * workspace learns who it is; it does not tell the API.
   */
  tenantId: string | null;
  userId: string | null;
  role: string | null;
}

/**
 * Reads the source from the page's own query string.
 *
 * Nothing here is authentication. Under `dev-header` the two id parameters are
 * the stand-in's inputs; under `cf-access` the browser's own Access session is
 * the credential and these are optional claims; under `disabled` a live read
 * answers 501. That is the seam working rather than a gap in this workspace
 * (ZD-002, ZD-115, ZD-116).
 */
export function sourceFromLocation(search: string): Source {
  const params = new URLSearchParams(search);
  return {
    base: params.has('fixture') ? null : (params.get('api') ?? ''),
    assessmentId: params.get('assessment'),
    tenantId: params.get('tenant'),
    userId: params.get('user'),
    role: params.get('role'),
  };
}

/** The identity claims a request carries: exactly the ones the URL stated. */
export const identityHeaders = (source: Source): Record<string, string> => ({
  ...(source.tenantId === null ? {} : { 'x-zevik-tenant': source.tenantId }),
  ...(source.userId === null ? {} : { 'x-zevik-user': source.userId }),
  ...(source.role === null ? {} : { 'x-zevik-role': source.role }),
});

async function read(
  source: Source,
  assessmentId: string,
  path: string,
  what: string,
): Promise<unknown> {
  const url =
    source.base === null
      ? 'fixtures/reviewer-workspace.json'
      : `${source.base.replace(/\/+$/u, '')}/v1/assessments/${assessmentId}${path}`;

  const response = await fetch(url, {
    headers: source.base === null ? {} : identityHeaders(source),
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new ReadFailedError(what, response.status, await detailOf(response));
  }
  return response.json();
}

/**
 * The API's own `{error}` body where there is one, so a refusal keeps its
 * words. Shared with `credential/api.ts`, where keeping the words is a rule
 * rather than a courtesy (ZD-118 §3).
 */
export async function detailOf(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (typeof body === 'object' && body !== null && 'error' in body) {
      return String(body.error);
    }
    return 'The response carried no message.';
  } catch {
    return 'The response carried no message.';
  }
}

/**
 * The whole workspace, from whichever source.
 *
 * Four reads live; one read from the fixture, which is why the fixture's shape
 * mirrors the routes rather than being flattened — the same code has to make
 * sense of both.
 */
export async function loadWorkspace(source: Source, assessmentId: string): Promise<Workspace> {
  if (source.base === null) {
    const raw = (await read(source, assessmentId, '', 'the fixture')) as {
      assessment: { assessment_id: string; state: string };
      findings: unknown;
      clarifications: ClarificationsResponse;
      review_log: unknown;
    };
    return {
      assessment: {
        assessment_id: raw.assessment.assessment_id,
        state: AssessmentStateSchema.parse(raw.assessment.state),
      },
      findings: parseFindings({ findings: raw.findings }, 'the fixture'),
      clarifications: raw.clarifications,
      reviewLog: parseReviewLog(raw.review_log),
    };
  }

  const [assessment, findings, clarifications, reviewLog] = await Promise.all([
    read(source, assessmentId, '', 'the assessment'),
    read(source, assessmentId, '/findings', 'the findings'),
    read(source, assessmentId, '/clarifications', 'the open questions'),
    read(source, assessmentId, '/review-log', 'the review log'),
  ]);

  return {
    assessment: parseAssessment(assessment),
    findings: parseFindings(findings, 'the findings'),
    clarifications: clarifications as ClarificationsResponse,
    reviewLog: parseReviewLog(reviewLog),
  };
}

/**
 * Records one decision.
 *
 * Against the fixture there is nothing to post to, and this refuses rather than
 * pretending: a workspace that showed a decision as recorded when nothing stored
 * it would be the accepting-and-discarding failure it exists to avoid.
 */
export async function recordDecision(
  source: Source,
  assessmentId: string,
  decision: DecisionRequest,
): Promise<{ decisions: ApproverDecision[] }> {
  if (source.base === null) {
    throw new DecisionRefusedError(
      0,
      'This workspace is reading a committed fixture, so there is nowhere to record a decision. ' +
        'Point it at a deployment with ?api=<base> to record one.',
    );
  }

  const response = await fetch(
    `${source.base.replace(/\/+$/u, '')}/v1/assessments/${assessmentId}/decisions`,
    {
      method: 'POST',
      headers: { ...identityHeaders(source), 'content-type': 'application/json' },
      body: JSON.stringify({ decisions: [decision] }),
      credentials: 'same-origin',
    },
  );
  if (!response.ok) {
    throw new DecisionRefusedError(response.status, await detailOf(response));
  }
  const body: unknown = await response.json();
  const recorded = (body as { decisions?: unknown }).decisions;
  return {
    decisions: Array.isArray(recorded)
      ? recorded.map((decision) => ApproverDecisionSchema.parse(decision))
      : [],
  };
}

/**
 * Records several decisions as one declared group (docs/DECISIONS.md ZD-127).
 *
 * The body is `{ batch, decisions }` — the declaration and its members —
 * and the API judges it as a set before any member is parsed. A refusal of
 * the set arrives as the request schema's issues and is thrown as
 * `BatchRefusedError` with them, so the screen can relay each sentence and
 * name the finding it is about; any other refusal is `DecisionRefusedError`
 * with the API's words, as for one decision.
 */
export async function recordBatch(
  source: Source,
  assessmentId: string,
  batch: BatchDeclaration,
  decisions: readonly DecisionRequest[],
): Promise<{ decisions: ApproverDecision[]; batch: DecisionBatch | null }> {
  if (source.base === null) {
    throw new DecisionRefusedError(
      0,
      'This workspace is reading a committed fixture, so there is nowhere to record a decision. ' +
        'Point it at a deployment with ?api=<base> to record one.',
    );
  }

  const response = await fetch(
    `${source.base.replace(/\/+$/u, '')}/v1/assessments/${assessmentId}/decisions`,
    {
      method: 'POST',
      headers: { ...identityHeaders(source), 'content-type': 'application/json' },
      body: JSON.stringify({ batch, decisions }),
      credentials: 'same-origin',
    },
  );
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const error =
      typeof body === 'object' && body !== null && 'error' in body
        ? String(body.error)
        : 'The response carried no message.';
    const issues =
      typeof body === 'object' && body !== null && 'issues' in body && Array.isArray(body.issues)
        ? (body.issues as BatchIssue[])
        : null;
    if (issues !== null) throw new BatchRefusedError(response.status, error, issues);
    throw new DecisionRefusedError(response.status, error);
  }
  const body = (await response.json()) as { decisions?: unknown; batch?: unknown };
  return {
    decisions: Array.isArray(body.decisions)
      ? body.decisions.map((decision) => ApproverDecisionSchema.parse(decision))
      : [],
    batch:
      body.batch === null || body.batch === undefined
        ? null
        : DecisionBatchSchema.parse(body.batch),
  };
}
